import { useRender } from '@base-ui/react/use-render'
import { type VariantProps } from 'class-variance-authority'
import { Children, isValidElement, use } from 'react'

import { cn } from '../../../../lib/utils.ts'

import { ItemActions } from './item-actions.tsx'
import { ItemGroupContext, type ItemSize } from './item-group-context.ts'
import { ItemMetadataContext, useCreateItemMetadataScope } from './item-metadata-context.ts'
import { itemRootFrameVariants, itemRootPrimaryVariants } from './item-root.variants.ts'
import { itemVariants } from './item.variants.ts'

export function ItemRoot(props: ItemRoot.Props): React.ReactElement {
  const { children, className, interactive = false, variant = 'default', size, ref, render, ...rest } = props
  const groupContext = use(ItemGroupContext)
  const metadataScope = useCreateItemMetadataScope()
  const { primaryChildren, actionsChildren } = partitionItemRootChildren(children)
  const resolvedSize = size ?? groupContext?.size ?? 'medium'
  const resolvedVariant = groupContext?.surfaceOwner === 'group' ? 'plain' : variant
  const isGrouped = groupContext !== undefined

  const primaryElement = useRender({
    render,
    ref,
    defaultTagName: 'div',
    props: {
      ...rest,
      'data-slot': 'item',
      className: cn(
        itemVariants({ variant: resolvedVariant, size: resolvedSize, interactive }),
        itemRootPrimaryVariants(),
        className,
      ),
      children: primaryChildren,
    },
  })

  return (
    <ItemMetadataContext.Provider value={metadataScope}>
      <div
        role={isGrouped ? 'listitem' : undefined}
        data-slot={isGrouped ? 'item-listitem' : 'item-frame'}
        className={itemRootFrameVariants()}
      >
        {primaryElement}
        {actionsChildren}
      </div>
    </ItemMetadataContext.Provider>
  )
}

export namespace ItemRoot {
  type Variants = NonNullable<VariantProps<typeof itemVariants>>
  export type Props = Omit<useRender.ComponentProps<'div'>, 'children' | 'ref'> & {
    children?: React.ReactNode
    ref?: React.Ref<HTMLElement> | undefined
    /** Surface treatment. @default 'default' */
    variant?: NonNullable<Variants['variant']> | undefined
    /** Vertical density. Inherits from Item.Group. @default 'medium' */
    size?: ItemSize | undefined
    /** Clickable row affordance. Independent from the rendered element. @default false */
    interactive?: boolean | undefined
  }
}

type ItemRootChildrenPartition = {
  primaryChildren: React.ReactNode[]
  actionsChildren: React.ReactNode[]
}

/**
 * Splits `Item.Actions` from the other children. The primary element can be a link or a label, so the actions are
 * rendered beside it rather than inside it: a nested button would otherwise trigger the row's own navigation or toggle.
 */
function partitionItemRootChildren(children: React.ReactNode): ItemRootChildrenPartition {
  const primaryChildren: React.ReactNode[] = []
  const actionsChildren: React.ReactNode[] = []

  for (const child of Children.toArray(children)) {
    if (isValidElement(child) && child.type === ItemActions) {
      actionsChildren.push(child)
    } else {
      primaryChildren.push(child)
    }
  }

  return { primaryChildren, actionsChildren }
}
